import React, {useCallback, useEffect, useRef, useState} from "react";
import {useAuth} from "../../context/user";
import {usePermissions} from "../../context/modulePermissions";
import {Layout} from "../../pages/siren/Layout";
import {AudioSlot, SirenPhoneCallConfig, getSirenPhoneCallConfigExact} from "./hooks/useGetSirenPhoneCallConfig";
import {useUpsertSirenPhoneCallConfig} from "./hooks/useUpsertSirenPhoneCallConfig";
import {useDeleteSirenPhoneCallConfig} from "./hooks/useDeleteSirenPhoneCallConfig";
import {ScopeSplitSelector, ScopeSelection} from "./components/ScopeSplitSelector";
import {IconButton} from "./import/inputs";
import {useScopeQueryParams} from "./hooks/useScopeQueryParams";
import {useSiren} from "../../pages/siren/SirenContext";
import {TextToSpeechPreview} from "../../pages/siren/components/TextToSpeechPreview";
import {BasicTextArea} from "../../components/form-v2/basic-text";
import {platformAPI, platformFormAPI} from "../../api/axiosConfig";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import {AudioPlayer} from "../../components/ui/AudioPlayer";

type AudioMode = "file" | "tts";

type AudioFieldState = {
    mode: AudioMode;
    ttsText: string;
    pendingFile: File | null;
};

const emptyAudioField = (): AudioFieldState => ({
    mode: "file",
    ttsText: "",
    pendingFile: null,
});

function audioFieldFromSlot(slot: AudioSlot | undefined): AudioFieldState {
    if (slot?.ttsText) return {mode: "tts", ttsText: slot.ttsText, pendingFile: null};
    return {mode: "file", ttsText: "", pendingFile: null};
}

// --- Audio field component ---

type AudioFieldProps = {
    label: string;
    description: string;
    value: AudioFieldState;
    slot: AudioSlot | undefined;
    recordID: string | undefined;
    audioType: string;
    disabled?: boolean;
    onChange: (val: AudioFieldState) => void;
};

const AudioField: React.FC<AudioFieldProps> = ({label, description, value, slot, recordID, audioType, disabled, onChange}) => {
    const {hasFullAccess} = usePermissions();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [localFileURL, setLocalFileURL] = useState<string | null>(null);

    useEffect(() => {
        if (!value.pendingFile) {
            setLocalFileURL(null);
            return;
        }
        const url = URL.createObjectURL(value.pendingFile);
        setLocalFileURL(url);
        return () => URL.revokeObjectURL(url);
    }, [value.pendingFile]);

    const hasAudio = !!slot?.audioPath;
    const streamURL = recordID && slot?.audioPath
        ? `api/siren/phone-call-config/${recordID}/audio/${audioType}?v=${encodeURIComponent(slot.updatedAt)}`
        : null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        onChange({...value, pendingFile: file});
        e.target.value = "";
    };

    const handleDownload = async () => {
        if (!streamURL || !recordID) return;
        try {
            const res = await platformAPI.get<Blob>(streamURL, {responseType: "blob"});
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `phone-call-${audioType}.mp3`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { /* ignore */ }
    };

    return (
        <div className="flex flex-col gap-2">
            <div>
                <p className="font-medium text-sm">{label}</p>
                <p className="text-xs text-light-grey">{description}</p>
            </div>
            <div className="flex gap-1 p-0.5 bg-light-light-grey rounded-md w-fit">
                <button
                    type="button"
                    disabled={disabled}
                    className={`px-3 py-1 text-xs rounded font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${value.mode === "file" ? "bg-white shadow-sm text-dark-dark-grey" : "text-light-grey hover:text-dark-dark-grey"}`}
                    onClick={() => onChange({...value, mode: "file"})}
                >
                    Upload File
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    className={`px-3 py-1 text-xs rounded font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${value.mode === "tts" ? "bg-white shadow-sm text-dark-dark-grey" : "text-light-grey hover:text-dark-dark-grey"}`}
                    onClick={() => onChange({...value, mode: "tts"})}
                >
                    Text-to-Speech
                </button>
            </div>

            {(slot?.status === "processing" || slot?.status === "pending") && <ProcessingBanner/>}

            {value.mode === "file" ? (
                <div className="flex flex-col gap-2">
                    <input ref={fileInputRef} type="file" accept=".mp3,.wav,.m4a" onChange={handleFileChange} className="hidden" disabled={disabled}/>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled={disabled}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-light-grey hover:bg-light-light-grey transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadIcon style={{fontSize: 14}}/>
                            {value.pendingFile ? value.pendingFile.name : "Choose file…"}
                        </button>
                        {hasAudio && !value.pendingFile && (
                            <button
                                type="button"
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-light-grey hover:bg-light-light-grey transition-colors"
                                onClick={handleDownload}
                            >
                                <DownloadIcon style={{fontSize: 14}}/>
                                Download
                            </button>
                        )}
                    </div>
                    {localFileURL && <AudioPlayer src={localFileURL}/>}
                    {hasAudio && slot?.status === "ready" && !value.pendingFile && streamURL && (
                        <AudioStreamPlayer url={streamURL}/>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    <BasicTextArea
                        label=""
                        value={value.ttsText}
                        onChange={v => onChange({...value, ttsText: v})}
                        disabled={disabled}
                    />
                    {hasFullAccess("siren_config") && <TextToSpeechPreview content={value.ttsText}/>}
                    {slot?.ttsText && slot?.status === "ready" && streamURL && (
                        <div className="flex flex-col gap-1">
                            <p className="text-xs text-light-grey">Generated audio:</p>
                            <AudioStreamPlayer url={streamURL}/>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-light-grey hover:bg-light-light-grey transition-colors"
                                    onClick={handleDownload}
                                >
                                    <DownloadIcon style={{fontSize: 14}}/>
                                    Download
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Fetches and plays a stream URL that requires auth
function AudioStreamPlayer({url}: {url: string}) {
    const [objectURL, setObjectURL] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const objectURLRef = useRef<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(false);
        let cancelled = false;
        platformAPI.get<Blob>(url, {responseType: "blob"})
            .then(res => {
                if (cancelled) return;
                const blobURL = URL.createObjectURL(res.data);
                objectURLRef.current = blobURL;
                setObjectURL(blobURL);
            })
            .catch(() => { if (!cancelled) setError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => {
            cancelled = true;
            if (objectURLRef.current) {
                URL.revokeObjectURL(objectURLRef.current);
                objectURLRef.current = null;
            }
        };
    }, [url]);

    if (loading) return <p className="text-xs text-light-grey">Loading audio…</p>;
    if (error) return <p className="text-xs text-red">Error loading audio</p>;
    if (objectURL) return <AudioPlayer src={objectURL}/>;
    return null;
}

function ProcessingBanner() {
    return (
        <div className="flex items-center gap-2 px-3 py-2 bg-white-blue rounded-md text-xs text-dark-blue font-medium">
            <svg className="animate-spin h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            Processing audio — this may take a moment…
        </div>
    );
}

// --- Single audio slot panel ---

type AudioSlotPanelProps = {
    audioType: string;
    slotKey: "preMessage" | "postDtmf" | "invalidOption" | "inboundCall" | "forwardingCall" | "alertNotFound" | "alreadyResponded";
    label: string;
    description: string;
    record: SirenPhoneCallConfig | null;
    scope: ScopeSelection;
    onRecordChange: (record: SirenPhoneCallConfig) => void;
};

const AudioSlotPanel: React.FC<AudioSlotPanelProps> = ({audioType, slotKey, label, description, record, scope, onRecordChange}) => {
    const {save, saving, error: saveError} = useUpsertSirenPhoneCallConfig();
    const {hasFullAccess} = usePermissions();

    const slot = record?.[slotKey];
    const [field, setField] = useState<AudioFieldState>(() => audioFieldFromSlot(slot));
    const [saved, setSaved] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [deletingSlot, setDeletingSlot] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    useEffect(() => {
        setField(audioFieldFromSlot(slot));
        setSaved(false);
    }, [record?.id, slot?.audioPath, slot?.ttsText]);

    // Poll while this slot is processing.
    useEffect(() => {
        if (slot?.status !== "processing" && slot?.status !== "pending") return;
        const interval = setInterval(async () => {
            try {
                const res = await platformAPI.get<SirenPhoneCallConfig>(`api/siren/phone-call-config/${record!.id}`);
                const updatedSlot = res.data[slotKey];
                if (updatedSlot?.status !== "processing" && updatedSlot?.status !== "pending") {
                    onRecordChange(res.data);
                }
            } catch { /* ignore */ }
        }, 3000);
        return () => clearInterval(interval);
    }, [record?.id, slot?.status]);

    const handleSave = useCallback(async () => {
        setSaved(false);
        setUploadError(null);
        try {
            const slotInput = field.mode === "tts" && field.ttsText ? {ttsText: field.ttsText} : undefined;
            const result = await save({
                clientID: scope.client?.id,
                departmentID: scope.department?.id,
                [slotKey]: slotInput,
            });

            if (field.mode === "file" && field.pendingFile) {
                setUploading(true);
                try {
                    const fd = new FormData();
                    fd.append("file", field.pendingFile);
                    const uploadRes = await platformFormAPI.post<SirenPhoneCallConfig>(
                        `api/siren/phone-call-config/${result.id}/audio/${audioType}`, fd
                    );
                    onRecordChange(uploadRes.data);
                    setField(prev => ({...prev, pendingFile: null}));
                } catch {
                    setUploadError("Failed to upload audio file");
                    onRecordChange(result);
                } finally {
                    setUploading(false);
                }
            } else {
                onRecordChange(result);
            }

            setSaved(true);
        } catch {
            // error shown via saveError
        }
    }, [scope, audioType, slotKey, field, save, onRecordChange]);

    const handleDeleteSlot = useCallback(async () => {
        if (!record) return;
        setDeleteConfirm(false);
        setDeletingSlot(true);
        try {
            const res = await platformAPI.delete<SirenPhoneCallConfig>(`api/siren/phone-call-config/${record.id}/audio/${audioType}`);
            onRecordChange(res.data);
        } catch { /* ignore */ } finally {
            setDeletingSlot(false);
        }
    }, [record, audioType, onRecordChange]);

    const isProcessing = slot?.status === "processing" || slot?.status === "pending";
    const isBusy = saving || uploading || isProcessing || deletingSlot;
    const hasInput = field.mode === "file" ? !!field.pendingFile : field.ttsText.trim().length > 0;
    const canSave = hasInput || !!slot;

    return (
        <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-4">
            <AudioField
                label={label}
                description={description}
                value={field}
                slot={slot}
                recordID={record?.id}
                audioType={audioType}
                disabled={isProcessing}
                onChange={isProcessing ? () => {} : setField}
            />
            {(saveError || uploadError) && (
                <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError ?? uploadError}</div>
            )}
            {saved && !isBusy && (
                <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Saved</div>
            )}
            {hasFullAccess("siren_config") && (
                <div className="flex justify-end gap-2">
                    {slot && (
                        <IconButton text="Delete" icon={<DeleteIcon style={{fontSize: 16}}/>} color="red" className="w-28" onClick={() => setDeleteConfirm(true)} disabled={isBusy}/>
                    )}
                    <IconButton
                        text={slot ? "Update" : "Create"}
                        icon={slot ? <SaveIcon style={{fontSize: 16}}/> : <AddIcon style={{fontSize: 16}}/>}
                        className="w-28"
                        onClick={handleSave}
                        loading={saving || uploading}
                        disabled={isBusy || !canSave}
                    />
                </div>
            )}

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete {label}</h3>
                        <p className="text-sm">Remove the audio for <span className="font-semibold">{label}</span>?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={deletingSlot} onClick={handleDeleteSlot}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Call forwarding panel ---

type CallForwardingPanelProps = {
    record: SirenPhoneCallConfig | null;
    scope: ScopeSelection;
    onRecordChange: (record: SirenPhoneCallConfig) => void;
};

const CallForwardingPanel: React.FC<CallForwardingPanelProps> = ({record, scope, onRecordChange}) => {
    const {save, saving, error: saveError} = useUpsertSirenPhoneCallConfig();
    const {hasFullAccess} = usePermissions();
    const [enabled, setEnabled] = useState(record?.callForwardingEnabled ?? false);
    const [number, setNumber] = useState(record?.callForwardingNumber ?? "");
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setEnabled(record?.callForwardingEnabled ?? false);
        setNumber(record?.callForwardingNumber ?? "");
        setSaved(false);
    }, [record?.id]);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: scope.client?.id,
                departmentID: scope.department?.id,
                callForwardingEnabled: enabled,
                callForwardingNumber: number,
            });
            onRecordChange(result);
            setSaved(true);
        } catch { /* error shown via saveError */ }
    }, [scope, enabled, number, save, onRecordChange]);

    return (
        <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-4">
            <div>
                <p className="font-medium text-sm">Call Forwarding</p>
                <p className="text-xs text-light-grey">Forward inbound calls to an external number</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={e => setEnabled(e.target.checked)}
                    className="w-4 h-4 rounded"
                />
                <span className="text-sm">Enable call forwarding</span>
            </label>
            {enabled && (
                <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-dark-grey">Forwarding number</label>
                    <input
                        type="tel"
                        value={number}
                        onChange={e => setNumber(e.target.value)}
                        placeholder="+441234567890"
                        className="border border-light-grey rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue"
                    />
                </div>
            )}
            {saveError && (
                <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>
            )}
            {saved && !saving && (
                <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Saved</div>
            )}
            {hasFullAccess("siren_config") && (
                <div className="flex justify-end">
                    <IconButton
                        text="Save"
                        icon={<SaveIcon style={{fontSize: 16}}/>}
                        className="w-28"
                        onClick={handleSave}
                        loading={saving}
                    />
                </div>
            )}
        </div>
    );
};

// --- All slots panel ---

type PhoneCallConfigPanelProps = {
    scope: ScopeSelection;
};

const PhoneCallConfigPanel: React.FC<PhoneCallConfigPanelProps> = ({scope}) => {
    const [record, setRecord] = useState<SirenPhoneCallConfig | null>(null);
    const {deleteConfig, deleting} = useDeleteSirenPhoneCallConfig();
    const {hasFullAccess} = usePermissions();
    const [loading, setLoading] = useState(true);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    useEffect(() => {
        setLoading(true);
        const params: Record<string, string> = {};
        if (scope.client) params.clientID = scope.client.id;
        if (scope.department) params.departmentID = scope.department.id;
        getSirenPhoneCallConfigExact(params)
            .then(setRecord)
            .finally(() => setLoading(false));
    }, [scope.client?.id, scope.department?.id]);

    const handleDelete = useCallback(async () => {
        if (!record) return;
        setDeleteConfirm(false);
        try {
            await deleteConfig(record.id);
            setRecord(null);
        } catch { /* silently ignore */ }
    }, [record, deleteConfig]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <div className={`flex flex-col gap-4 p-4 overflow-y-auto${!hasFullAccess("siren_config") ? " pointer-events-none" : ""}`}>
            <div>
                <p className="font-semibold text-sm">{scope.client ? scope.client.name : "System Default"}</p>
                <p className="text-xs text-light-grey">{scope.client ? (scope.department ? scope.department.name : "Client level") : "Global fallback — no client"}</p>
            </div>

            <CallForwardingPanel
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />
            <AudioSlotPanel
                audioType="preMessage"
                slotKey="preMessage"
                label="Pre-message Audio"
                description="Played before the alert message"
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />
            <AudioSlotPanel
                audioType="postDtmf"
                slotKey="postDtmf"
                label="Post-DTMF Audio"
                description="Played after a DTMF response is received"
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />
            <AudioSlotPanel
                audioType="invalidOption"
                slotKey="invalidOption"
                label="Invalid Option Audio"
                description="Played when an invalid option is pressed"
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />
            <AudioSlotPanel
                audioType="inboundCall"
                slotKey="inboundCall"
                label="Inbound Call Audio"
                description="Played when an inbound call is received"
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />
            <AudioSlotPanel
                audioType="forwardingCall"
                slotKey="forwardingCall"
                label="Forwarding Call Audio"
                description="Played when a call is being forwarded"
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />
            <AudioSlotPanel
                audioType="alertNotFound"
                slotKey="alertNotFound"
                label="Alert Not Found Audio"
                description="Played when no active alert is found for the caller"
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />
            <AudioSlotPanel
                audioType="alreadyResponded"
                slotKey="alreadyResponded"
                label="Already Responded Audio"
                description="Played when the caller has already responded to their alert (press 1 to replay the alert)"
                record={record}
                scope={scope}
                onRecordChange={setRecord}
            />

            {record && hasFullAccess("siren_config") && (
                <div className="flex justify-end">
                    <IconButton text="Delete" icon={<DeleteIcon style={{fontSize: 16}}/>} color="red" className="w-28" onClick={() => setDeleteConfirm(true)} disabled={deleting}/>
                </div>
            )}

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete Phone Call Config</h3>
                        <p className="text-sm">Delete all audio config for <span className="font-semibold">{scope.client ? scope.client.name : "System Default"}</span>{scope.department ? <> / <span className="font-semibold">{scope.department.name}</span></> : ""}?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={deleting} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Page ---

const SirenPhoneCallConfigPage: React.FC = () => {
    const {clientID} = useSiren();
    const {isSuperAdmin, isDepartmentAdmin} = useAuth();
    const {scope: selectedScope, drillClientID, drillClientName, deptPage, onSelect, onDrill, onDeptPageChange, onClear} = useScopeQueryParams({defaultClientID: isSuperAdmin ? null : clientID});

    return (
        <Layout menuCurrent="config">
            <div className="flex gap-4 h-full">
                <div className="w-[460px] shrink-0">
                    <ScopeSplitSelector
                        selectedScope={selectedScope}
                        onSelect={onSelect}
                        onClear={onClear}
                        onDrill={onDrill}
                        initialClientID={drillClientID}
                        initialClientName={drillClientName}
                        deptPage={deptPage}
                        onDeptPageChange={onDeptPageChange}
                        hydrate="phoneCallConfigExistence"
                        showSystemDefault={isSuperAdmin}
                        showClientLevel={!isDepartmentAdmin}
                    />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {selectedScope ? (
                        <PhoneCallConfigPanel
                            key={`${selectedScope.client?.id ?? "system"}-${selectedScope.department?.id ?? "client"}`}
                            scope={selectedScope}
                        />
                    ) : (
                        <div className="p-5 text-sm text-light-grey">
                            Select a client, then a scope to configure.
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default SirenPhoneCallConfigPage;
